import React, { useEffect, useState } from 'react';
import { useIsFocused } from '@react-navigation/native';
import lodash from 'lodash';
import debugFactory from 'debug';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useParams } from 'react-router-dom';
import { Platform } from 'react-native';
import { useSelector } from 'react-redux';

import { RouteNames } from '../../navigation/ExploreNavigator';
import { ExploreScreen } from '../../zonetail-explore-library';
import {
  FullCategory,
  FullThumbtackCategory,
  Tag,
  Provider,
} from '../../api/adserver';
import { CONDO_CORP_NAME, DEFAULT_BUILDING } from '../../lib/constants';
import ExploreHeader from '../../web/components/ExploreHeader';
import analytics from '../../lib/wrapAnalytics';
import FutrHeroBanner from '../../components/FutrHeroBanner/FutrHeroBanner';
import { selectPromotedFeatureAd } from '../../store/advertsSlice';
import { RootState } from '../../store/rootReducer';

const debug = debugFactory('se:landingtest');

interface Params {
  building_id: string;
}

const LandingScreen = ({ navigation, route }: any) => {
  const promotedAd = useSelector((state: RootState) => selectPromotedFeatureAd(state));
  const startupQuery = lodash.get(route, 'params.query', null);
  const startupTag = lodash.get(route, 'params.tag', null);
  //const building_id = lodash.get(route, 'params.building_id', null);
  let building_id: string | null = null;

  if (Platform.OS === 'web') {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    ({ building_id } = useParams<Params>());
  }

  useEffect(() => {
    if (Platform.OS === 'web') {
      if (building_id) {
        AsyncStorage.setItem(CONDO_CORP_NAME, building_id).then(() => {
          console.log(`CONDO_CORP_NAME: ${building_id}`);
        });
      } else {
        AsyncStorage.setItem(CONDO_CORP_NAME, DEFAULT_BUILDING).then(() => {
          console.log(`CONDO_CORP_NAME: ${DEFAULT_BUILDING}`);
        });
      }
    }
  }, [building_id]);

  console.log('building_id is:', building_id);
  console.log('route is:', route);
  console.log('navigation is:', navigation);

  const isFocused = useIsFocused();

  // this is like deep linking
  useEffect(() => {
    // if we were passed a query string, then show the results screen to the user
    // the results screen will clear the startupQuery for us.
    if (isFocused) {
      if (startupQuery) {
        let tag;
        if (startupTag) {
          tag = JSON.parse(startupTag);
        }
        debug(
          `navigating to nested screen: ${JSON.stringify({
            startupQuery,
            tag,
          })}`,
        );
        navigation.navigate(RouteNames.Results, { query: startupQuery, tag });
        navigation.setParams({ query: undefined, tag: undefined });
      }
    }
  }, [navigation, startupQuery, startupTag, isFocused]);

  // const [corpName, setCorpName] = useState<null | string>(null);
  // useEffect(() => {
  //   const fetchCorpName = async () => {
  //     try {
  //       const name = await AsyncStorage.getItem(CONDO_CORP_NAME);
  //       setCorpName(name);
  //     } catch (error) {
  //       console.log(error);
  //     }
  //   };
  //   fetchCorpName();
  // }, [setCorpName]);

  const [corpName, setCorpName] = useState<null | string>(null);
  useEffect(() => {
    const fetchCorpName = async () => {
      try {
        const name = await AsyncStorage.getItem(CONDO_CORP_NAME);
        if (!name) {
          // force to corp name for web only with no auth
          // allow building_id param to set corp building id, if set
          let corpBuildingId = DEFAULT_BUILDING;
          if (building_id) {
            corpBuildingId = building_id;
          }
          analytics.trackProperty('WebUser', 'WebUser', corpBuildingId);
          setCorpName(corpBuildingId);
        } else {
          setCorpName(name);
        }
      } catch (error) {
        console.log(error);
      }
    };
    fetchCorpName();
  }, [setCorpName, building_id]);

  const onCategoryClick = (item: FullCategory) => {
    navigation.navigate(RouteNames.Results, {
      query: item.title,
      age: item.age_gated_min_age,
    });
  };

  const verifyFindMoreFullThumbtackCategory = (
    item: FullThumbtackCategory,
    thumbtackCategoryTapped?: string,
  ) => {
    if (item.adverts) {
      if (thumbtackCategoryTapped) {
        return thumbtackCategoryTapped;
      }
      // if (
      //   lodash.isArray(item.adverts) &&
      //   !lodash.isEmpty(item.adverts) &&
      //   item.adverts[0].categoryDisplayName
      // ) {
      //   return item.adverts[0].categoryDisplayName;
      // }
    }
    return null;
  };

  const onFindMoreClick = (
    item: FullCategory | FullThumbtackCategory,
    provider: Provider = Provider.Zonetail,
    thumbtackCategoryTapped?: string,
  ) => {
    console.log('on find more', item.title);
    let itemTitle = item.title;
    if (provider === Provider.Thumbtack) {
      const thumbtackCategoryName = verifyFindMoreFullThumbtackCategory(
        item as FullThumbtackCategory,
        thumbtackCategoryTapped,
      );
      if (thumbtackCategoryName) {
        itemTitle = thumbtackCategoryName;
      }
    } else if (provider === Provider.ThumbtackCategory) {
      const findMoreThumbtackTitle = 'All Categories';
      itemTitle = findMoreThumbtackTitle;
    }
    navigation.navigate(RouteNames.Results, { query: itemTitle, provider });
  };

  const onSearchResultClick = (title: string) => {
    navigation.navigate(RouteNames.Results, { query: title });
  };

  const onTagClick = (title: string, tag: Tag) => {
    navigation.navigate(RouteNames.Results, {
      query: title,
      tag,
    });
  };

  if (!corpName) {
    return null;
  }

  const renderPromotedBanner = promotedAd
    ? () => (
        <FutrHeroBanner
          heroImageUrl={promotedAd.hero_image?.url ?? '/images/futr-hero-alt.png'}
          logoUrl={promotedAd.carousel_images?.[0]?.url ?? '/images/futr-zonetail-logos.png'}
          headline={promotedAd.headline ?? 'Rent is your first step to owning a home.'}
          byline={promotedAd.bi_line ?? 'Let your FUTR Agent get you there.'}
          ctaText={promotedAd.actions?.[0]?.title ?? 'Get Started'}
          ctaUrl={promotedAd.actions?.[0]?.url ?? 'https://futrnetwork.com/zonetail'}
        />
      )
    : undefined;

  if (Platform.OS === 'web') {
    return (
      <>
        <ExploreHeader />
        <ExploreScreen
          onCategoryClick={onCategoryClick}
          onFindMoreClick={onFindMoreClick}
          onSearchResultClick={onSearchResultClick}
          onTagClick={onTagClick}
          corpName={corpName}
          renderPromotedBanner={renderPromotedBanner}
        />
      </>
    );
  }

  return (
    <ExploreScreen
      onCategoryClick={onCategoryClick}
      onFindMoreClick={onFindMoreClick}
      onSearchResultClick={onSearchResultClick}
      onTagClick={onTagClick}
      corpName={corpName}
      renderPromotedBanner={renderPromotedBanner}
    />
  );
};

export default LandingScreen;
